import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import { typeLabel } from "@/utils/media";
import PlayNoticeModal from "@/components/feature/PlayNoticeModal";

export default function HeroCarousel({ items }: { items: Movie[] }) {
  const [index, setIndex] = useState(0);
  const [noticeOpen, setNoticeOpen] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  const goPrev = () => setIndex((prev) => (prev - 1 + items.length) % items.length);
  const goNext = () => setIndex((prev) => (prev + 1) % items.length);
  const current = items[index];

  return (
    <section className="relative w-full overflow-hidden rounded-lg bg-secondary-950">
      <div className="relative h-[440px] w-full md:h-[560px]">
        {items.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={item.heroImage}
              alt={`${item.title} 剧照`}
              title={`${item.title} - ${typeLabel(item.type)} - 蘑菇视频`}
              className="h-full w-full object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-transparent" />
          </div>
        ))}

        <div className="absolute inset-0 flex items-center">
          <div className="w-full max-w-[1320px] px-6 md:px-12">
            <div className="max-w-xl">
              <div className="flex items-center gap-2">
                <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                  {typeLabel(current.type)}
                </span>
                <span className="flex items-center gap-1 rounded-md bg-white/15 px-2.5 py-1 text-xs font-semibold text-primary-200 backdrop-blur">
                  <i className="ri-star-fill text-[11px]" />
                  {current.rating.toFixed(1)}
                </span>
                <span className="rounded-md bg-white/15 px-2.5 py-1 text-xs text-background-100 backdrop-blur">
                  {current.year}
                </span>
              </div>

              <h2 className="mt-4 font-heading text-3xl font-extrabold text-white md:text-5xl">
                {current.title}
              </h2>

              <p className="mt-3 line-clamp-2 max-w-lg text-sm leading-relaxed text-background-100/85 md:text-base">
                {current.description}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setNoticeOpen(true)}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-fill text-lg" />
                  立即播放
                </button>
                <Link
                  to={`/detail/${current.id}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-white/40 bg-white/10 px-6 py-3 text-sm font-medium text-white backdrop-blur transition-colors hover:bg-white/20"
                >
                  <i className="ri-information-line text-lg" />
                  查看详情
                </Link>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={goPrev}
          aria-label="上一张"
          className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur transition-colors hover:bg-primary-500 md:flex"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          onClick={goNext}
          aria-label="下一张"
          className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur transition-colors hover:bg-primary-500 md:flex"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 张`}
              onClick={() => setIndex(i)}
              className={`h-2 rounded-full transition-all ${
                i === index ? "w-7 bg-primary-500" : "w-2 bg-white/50 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      </div>

      <PlayNoticeModal open={noticeOpen} onClose={() => setNoticeOpen(false)} title={current.title} />
    </section>
  );
}